// OpenAI API Service for GPT-5-mini with real API integration

interface OpenAIResponse {
  success: boolean;
  data?: any;
  error?: string;
  source: 'api' | 'cache' | 'fallback';
}

interface RateLimitState {
  requests: number;
  resetTime: number;
}

class OpenAIService {
  private apiKey: string;
  private baseUrl = 'https://api.openai.com/v1';
  private rateLimitState: Map<string, RateLimitState> = new Map();
  private responseCache: Map<string, { data: any; timestamp: number }> = new Map();

  constructor() {
    this.apiKey = ''; // API key now handled securely by Supabase Edge Function
    this.baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
    this.validateConfiguration();
  }

  private validateConfiguration(): void {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.error('VITE_SUPABASE_URL not found - cannot access edge functions');
    } else {
      console.log('✅ OpenAI service initialized - using Supabase Edge Function at', this.baseUrl);
    }
  }

  private getCacheKey(endpoint: string, params: any): string {
    return `${endpoint}:${JSON.stringify(params)}`;
  }

  private isRateLimited(endpoint: string): boolean {
    const state = this.rateLimitState.get(endpoint);
    if (!state) return false;

    const now = Date.now();
    if (now > state.resetTime) {
      this.rateLimitState.delete(endpoint);
      return false;
    }

    return state.requests >= 60; // OpenAI rate limit: 60 requests per minute
  }

  private updateRateLimit(endpoint: string): void {
    const now = Date.now();
    const resetTime = now + (60 * 1000); // 1 minute from now
    
    const state = this.rateLimitState.get(endpoint);
    if (!state || now > state.resetTime) {
      this.rateLimitState.set(endpoint, { requests: 1, resetTime });
    } else {
      state.requests++;
    }
  }

  private getCachedResponse(cacheKey: string): any | null {
    const cached = this.responseCache.get(cacheKey);
    if (!cached) return null;

    const cacheAge = Date.now() - cached.timestamp;
    const maxAge = 5 * 60 * 1000; // 5 minutes cache

    if (cacheAge > maxAge) {
      this.responseCache.delete(cacheKey);
      return null;
    }

    return cached.data;
  }

  private setCachedResponse(cacheKey: string, data: any): void {
    this.responseCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
  }

  // GPT-5-mini Text Generation
  async generateText(prompt: string, options: { 
    temperature?: number; 
    maxTokens?: number; 
    tone?: string;
  } = {}): Promise<OpenAIResponse> {
    const endpoint = 'chat/completions';
    const cacheKey = this.getCacheKey(endpoint, { prompt, options });

    // Check cache first
    const cached = this.getCachedResponse(cacheKey);
    if (cached) {
      return { success: true, data: cached, source: 'cache' };
    }

    // Check rate limiting
    if (this.isRateLimited(endpoint)) {
      return {
        success: false,
        error: 'RATE_LIMIT_EXCEEDED',
        source: 'api'
      };
    }

    try {
      this.updateRateLimit(endpoint);
      
      let requestBody = {
        model: 'gpt-5-mini',
        max_tokens: options.maxTokens,
        // reasoning_effort removed - causes GPT-5 to use all tokens for reasoning, leaving none for output
        // Note: temperature not supported by GPT-5 models - defaults to 1
        messages: [
          {
            role: 'system',
            content: `You are an expert nonprofit communication specialist helping Maya Rodriguez, Program Coordinator at Hope Gardens Community Center.

CRITICAL RESPONSE RULES:
- Keep responses to 2-3 sentences MAXIMUM
- End with a relevant follow-up question to continue dialogue
- Focus on ONE aspect at a time, not everything at once
- Be specific to Hope Gardens context, not generic advice
- ${
              options.tone === 'professional' ? 'Use professional, warm tone appropriate for nonprofit communications.' :
              options.tone === 'strategic' ? 'Use strategic, data-driven language for board presentations.' :
              'Use clear, engaging language that connects with the audience.'
            }`
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      };
      
      console.log('🚀 Making request to:', `${this.baseUrl}/openai-chat-completion`);
      console.log('🚀 Request body:', requestBody);

      const response = await fetch(`${this.baseUrl}/openai-chat-completion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log('🚀 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('OpenAI API Error Details:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`OpenAI API Error: ${response.status} ${response.statusText} - ${errorData}`);
      }

      const data = await response.json();
      
      console.log('🔍 RAW EDGE FUNCTION RESPONSE:', data);
      
      if (data.error) {
        console.error('❌ Edge Function returned error:', data.error);
        throw new Error(data.error);
      }
      
      // Extract content with multiple fallback paths
      let generatedText = '';

      console.log('🔍 RESPONSE STRUCTURE DEBUG:', {
        hasChoices: !!data.choices,
        choicesLength: data.choices?.length,
        firstChoice: data.choices?.[0],
        messageObject: data.choices?.[0]?.message,
        messageKeys: data.choices?.[0]?.message ? Object.keys(data.choices[0].message) : 'no message',
        content: data.choices?.[0]?.message?.content,
        refusal: data.choices?.[0]?.message?.refusal,
        text: data.choices?.[0]?.message?.text,
        output: data.choices?.[0]?.message?.output,
        allMessageData: JSON.stringify(data.choices?.[0]?.message)
      });

      if (data.choices && data.choices.length > 0) {
        const choice = data.choices[0];
        const message = choice?.message;

        // Log every field in the message to find where content is
        console.log('🔍 MESSAGE FIELDS:', {
          role: message?.role,
          content: message?.content,
          contentType: typeof message?.content,
          contentIsEmpty: message?.content === '',
          refusal: message?.refusal,
          annotations: message?.annotations,
          toolCalls: message?.tool_calls,
          allKeys: message ? Object.keys(message) : [],
          fullMessage: message,
          choiceKeys: choice ? Object.keys(choice) : [],
          finishReason: choice?.finish_reason,
          choiceIndex: choice?.index,
          choiceText: choice?.text,
          choiceMessage: choice?.message
        });

        // Check for problematic finish_reason
        if (choice?.finish_reason && choice.finish_reason !== 'stop') {
          console.warn('⚠️ Unusual finish_reason:', choice.finish_reason, '- content might be truncated or filtered');
        }

        // GPT-5-mini might use different field - try multiple locations
        generatedText = message?.content ||
                       choice?.text ||
                       message?.text ||
                       message?.refusal ||
                       '';
      } else if (data.generated_text) {
        generatedText = data.generated_text;
      } else if (data.content) {
        generatedText = data.content;
      } else if (data.text) {
        generatedText = data.text;
      }

      console.log('🔍 EXTRACTED CONTENT:', {
        generatedText: generatedText.substring(0, 200),
        contentLength: generatedText.length,
        hasChoices: !!data.choices,
        choicesLength: data.choices?.length,
        model: data.model || requestBody.model,
        usage: data.usage
      });

      // If no content, throw error (no fallback to other models)
      if (!generatedText) {
        console.error('❌ GPT-5-mini returned empty content after Edge Function deployment');
        throw new Error('GPT-5-mini generation failed - check Edge Function logs and token configuration');
      }

      const processedData = {
        generated_text: generatedText,
        usage: data.usage,
        model: data.model || requestBody.model,
        source: 'supabase-edge-function'
      };

      this.setCachedResponse(cacheKey, processedData);
      return { success: true, data: [processedData], source: 'api' };

    } catch (error) {
      console.error('OpenAI API call failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'API_CALL_FAILED',
        source: 'api'
      };
    }
  }

  // Enhanced Prompt Enhancement (using GPT-5-mini)
  async enhancePrompt(prompt: string): Promise<OpenAIResponse> {
    const enhancementPrompt = `
Enhance this nonprofit communication prompt to get better AI results. 

Original prompt: "${prompt}"

Please rewrite this prompt to include:
1. Clear context about the nonprofit organization
2. Specific audience information
3. Desired tone and voice
4. Expected outcome or action
5. Any necessary constraints

Return only the enhanced prompt, nothing else.`;

    const response = await this.generateText(enhancementPrompt, {
      temperature: 0.3,
      maxTokens: 300,
      tone: 'strategic'
    });

    if (response.success && response.data) {
      const enhancement = {
        original: prompt,
        enhanced: response.data[0]?.generated_text || prompt,
        improvements: [
          'Added nonprofit context',
          'Specified audience targeting',
          'Clarified tone requirements',
          'Defined success criteria',
          'Set appropriate constraints',
        ],
        confidence: 0.9,
        model: 'gpt-5-mini'
      };

      return { success: true, data: enhancement, source: response.source };
    }

    return response;
  }

  // Quality evaluation for AI outputs
  async evaluateQuality(text: string, criteria: string[]): Promise<OpenAIResponse> {
    const evaluationPrompt = `
Evaluate this nonprofit communication for Maya Rodriguez (Program Director):

Text to evaluate: "${text}"

Rate each criterion on a scale of 1-10 and provide brief feedback:
${criteria.map(criterion => `- ${criterion}`).join('\n')}

Return a JSON object with scores and feedback.`;

    return this.generateText(evaluationPrompt, {
      temperature: 0.2,
      maxTokens: 400,
      tone: 'professional'
    });
  }

  private getFallbackResponse(prompt: string, options: any): OpenAIResponse {
    return {
      success: false,
      error: 'AI_GENERATION_FAILED',
      source: 'fallback'
    };
  }

  // Health Check
  async checkHealth(): Promise<{ status: 'healthy' | 'degraded' | 'down'; details: any }> {
    if (!this.apiKey) {
      return {
        status: 'down',
        details: { error: 'No API key configured' }
      };
    }

    try {
      const response = await fetch(`${this.baseUrl}/models`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
        },
      });

      return {
        status: response.ok ? 'healthy' : 'degraded',
        details: {
          api: response.ok ? 'operational' : `HTTP ${response.status}`,
          rateLimit: this.getRateLimitStatus(),
          cacheSize: this.responseCache.size,
        }
      };
    } catch (error) {
      return {
        status: 'down',
        details: {
          error: error instanceof Error ? error.message : 'Unknown error',
          fallbackActive: true,
        }
      };
    }
  }

  private getRateLimitStatus(): any {
    const status: any = {};
    for (const [endpoint, state] of this.rateLimitState.entries()) {
      status[endpoint] = {
        requests: state.requests,
        resetIn: Math.max(0, state.resetTime - Date.now()),
      };
    }
    return status;
  }

  clearCache(): void {
    this.responseCache.clear();
    this.rateLimitState.clear();
  }
}

export const openAIService = new OpenAIService();